/* VIERA / Nanaimo Fire Rescue skill-sheet manifest (#1013).
 *
 * VIERA (the Vancouver Island Emergency Response Academy) publishes the NFPA
 * 1001 practical checksheets and completed-evaluation summary forms a candidate
 * studies and an evaluator signs against. We MIRROR them (download once, store
 * in our own bucket, serve through the api) rather than hotlinking Nanaimo's
 * deep `/docs/services/emergency-services/…` paths, which break if they
 * reorganize.
 *
 * This module is the single source of truth for the mirror, consumed by:
 *   - bin/skill-sheets/fetch.js  — downloads each `file` from SOURCE_BASE
 *   - api/src/routes/skillSheets.js — validates slugs + serves <slug>.pdf
 *   - src/data/skillSheets.js (client) — which sheet links to render
 *
 * Each entry maps OUR stable `slug` (the public api path) to Nanaimo's source
 * filename — we never expose their URL slugs. `nfpaChapter` lets the matrix UI
 * look up the checksheet/summary for a chapter header. `edition` + `fetchedAt`
 * (recorded by the fetch script alongside the file, see fetch.js) let us detect
 * drift when VIERA bumps an edition. */

// All mirrored docs live under one Nanaimo path; entries store just the filename.
const SOURCE_BASE = 'https://www.nanaimo.ca/docs/services/emergency-services/';

const NFPA_1001 = 'NFPA 1001 (2019)';

// The edition we currently mirror + serve. This is a FOLDER name: every sheet
// lives at <CURRENT_EDITION>/<slug>.pdf in the bucket and the local tree, so a
// complete snapshot of an edition is self-contained in one folder. When VIERA
// reissues (e.g. NFPA 1010, 2024), bump this to a new folder, re-fetch into it,
// and the old edition stays served from its own folder untouched — nothing is
// overwritten, and pinned sign-offs keep resolving to the document they were
// evaluated against. CURRENT_EDITION_LABEL is the human string for the UI.
const CURRENT_EDITION = '1001-2019';
const CURRENT_EDITION_LABEL = NFPA_1001;

// Edition folders are simple identifiers — no dots/slashes — so they can't be
// used to traverse out of the storage prefix. The api validates against this.
const EDITION_RE = /^[A-Za-z0-9_-]+$/;

// Storage/object path for a sheet within an edition folder. Defaults to the
// current edition; pass a past edition (e.g. '1001-2019') to pin one.
const skillSheetPath = (slug, edition = CURRENT_EDITION) => `${edition}/${slug}.pdf`;

// Codes that have a standalone per-skill PDF, split out of the chapter
// checksheet ({edition: [codes]}). Generated from the mirrored PDFs by
// bin/skill-sheets/split-skills.mjs; the split PDFs live at
// <edition>/skills/<code>.pdf. The try/catch lets the split script load this
// module before the index exists on a first run.
let SKILL_SHEET_SKILLS = {};
try {
    SKILL_SHEET_SKILLS = require('./vieraSkillSheetSkills.json');
} catch {
    SKILL_SHEET_SKILLS = {};
}

// Whether a split per-skill sheet exists for this drill code in the edition.
const hasSkillSheet = (code, edition = CURRENT_EDITION) => (SKILL_SHEET_SKILLS[edition] || []).includes(code);

// Overlay-fill geometry for the completed-evaluation forms ({edition: {slug:
// {family, pages}}}). Generated against the mirrored PDFs by
// bin/skill-sheets/calibrate-forms.mjs (#1014); consumed by the api's
// evaluation-form builder. Same try/catch as the skills index so the
// calibration script can load this module before the file exists.
let SKILL_SHEET_GEOMETRY = {};
try {
    SKILL_SHEET_GEOMETRY = require('./vieraSkillSheetGeometry.json');
} catch {
    SKILL_SHEET_GEOMETRY = {};
}

// The fill geometry for one form, or null when the edition/slug isn't calibrated.
const getSkillSheetGeometry = (slug, edition = CURRENT_EDITION) => SKILL_SHEET_GEOMETRY[edition]?.[slug] || null;

// Storage/object path for a split per-skill PDF within an edition folder.
const skillSheetSkillPath = (code, edition = CURRENT_EDITION) => `${edition}/skills/${code}.pdf`;

// Practical checksheet + completed-evaluation summary form for one NFPA 1001
// chapter. Produces two manifest rows from one terse definition.
const chapter = (ch, label, checksheetFile, summaryFile) => [
    {slug: `ch${ch}-checksheet`, title: `Ch ${ch} · ${label} — Skill Sheet`, kind: 'checksheet', nfpaChapter: ch, edition: NFPA_1001, file: checksheetFile},
    {slug: `ch${ch}-summary`, title: `Ch ${ch} · ${label} — Evaluation Summary`, kind: 'summary', nfpaChapter: ch, edition: NFPA_1001, file: summaryFile}
];

// Priority 1 — NFPA 1001 practical checksheets + their summary forms.
const CHAPTERS = [
    chapter(2, 'FF Health & Safety', '1001-chapter-2-ff-health-safety.pdf', 'ch-2-ff-h-s.pdf'),
    chapter(3, 'PPE & SCBA', '1001-chapter-3-ppe.pdf', 'ch-3-ppe.pdf'),
    chapter(4, 'Communications', '1001-chapter-4-communications.pdf', 'ch-4-communications.pdf'),
    chapter(7, 'Portable Fire Extinguishers', '1001-chapter-7-portable-fire-extinguishers.pdf', 'ch-7-fire-ext.pdf'),
    chapter(8, 'Tools & Equipment', '1001-chapter-8-ff-tools-equipment.pdf', 'ch-8-tools-equip.pdf'),
    chapter(9, 'Ropes & Knots', '1001-chapter-9-ropes-knots.pdf', 'ch-9-ropes-knots.pdf'),
    chapter(10, 'Forcible Entry', '1001-chapter-10-forcible-entry.pdf', 'ch-10-forcible-entry.pdf'),
    chapter(11, 'Ladders', '1001-chapter-11-ladders.pdf', 'ch-11-ladders.pdf'),
    chapter(12, 'Search & Rescue', '1001-chapter-12-search-rescue.pdf', 'ch-12-search-rescue.pdf'),
    chapter(13, 'Ventilation', '1001-chapter-13-ventilation.pdf', 'ch-13-ventilation.pdf'),
    chapter(14, 'Water Supply', '1001-chapter-14-water-supply-systems.pdf', 'ch-14-water-supply.pdf'),
    chapter(15, 'Fire Hose, Appliances & Nozzles', '1001-chapter-15-fire-hose-appliances-nozzles.pdf', 'ch-15-fire-hose-app-nozz.pdf'),
    chapter(16, 'Supply/Attack Line Evolutions', '1001-chapter-16-supply-line-attack-line-evolutions.pdf', 'ch-16-supply-line-att-lin-evol.pdf'),
    chapter(17, 'Fire Suppression', '1001-chapter-17-fire-suppression.pdf', 'ch-17-fire-supp.pdf'),
    chapter(18, 'FF Survival', '1001-chapter-18-ff-survival.pdf', 'ch-18-ff-surv.pdf'),
    chapter(19, 'Salvage & Overhaul', '1001-chapter-19-salvage-overhaul.pdf', 'ch-19-salv-overhaul.pdf'),
    chapter(21, 'Wildland & Ground Cover', '1001-chapter-21-wildland-ground-cover-fires.pdf', 'ch-21-wildland.pdf'),
    chapter(22, 'Establishing/Transferring Command', '1001-chapter-22-establishing-transferring-command.pdf', 'ch-22-est-transf-command.pdf'),
    chapter(23, 'Advanced Fire Suppression', '1001-chapter-23-advanced-fire-suppression.pdf', 'ch-23-adv-fire-supp.pdf'),
    chapter(24, 'Vehicle Rescue & Extrication', '1001-chapter-24-vehicle-rescue-extrication.pdf', 'ch-24-veh-res-extric.pdf'),
    chapter(25, 'Assisting Special Rescue Teams', '1001-chapter-25-assisting-special-rescue-teams.pdf', 'ch-25-assist-spec-resc-team.pdf'),
    chapter(27, 'Fire & Life Safety Initiatives', '1001-chapter-27-fire-life-safety-initiatives.pdf', 'ch-27-fire-life-safe-init.pdf')
].flat();

// Cross-chapter completed-evaluation forms + live-fire references (Ch 17/23).
const FORMS = [
    {slug: 'eval-multiple-students', title: 'Completed Evaluations — Multiple Students', kind: 'form', edition: NFPA_1001, file: 'completed-evaluations-multiple-students-form.pdf'},
    {slug: 'eval-one-student', title: 'Completed Evaluations — One Student', kind: 'form', edition: NFPA_1001, file: 'completed-multiple-evaluations-one-student-form.pdf'},
    {slug: 'live-fire-requirements', title: 'Live Fire — VIERA NFPA 1001 Requirements', kind: 'reference', edition: NFPA_1001, file: 'live-fire-requirements-for-viera-nfpa-1001-fire-fighter.pdf'},
    {slug: 'live-fire-i', title: 'Live Fire I — Fire Suppression Evaluations', kind: 'reference', nfpaChapter: 17, edition: NFPA_1001, file: 'live-fire-i-fire-suppression-evaluations.pdf'},
    {slug: 'live-fire-ii', title: 'Live Fire II — Advanced Fire Suppression Evaluations', kind: 'reference', nfpaChapter: 23, edition: NFPA_1001, file: 'live-fire-ii-advanced-fire-suppression-evaluations.pdf'}
];

// Priority 2 — candidate/evaluator orientation docs (section landing pages).
const ORIENTATION = [
    {slug: 'candidate-guidebook', title: 'VIERA002 — Candidate Guidebook', kind: 'reference', file: 'viera002.pdf'},
    {slug: 'evaluator-manual', title: 'VIERA005 — Evaluator Manual', kind: 'reference', file: 'viera005.pdf'},
    {slug: 'general-request-form', title: 'VIERA — General Request Form', kind: 'form', file: 'viera-general-request-form.pdf'}
];

const VieraSkillSheets = [...CHAPTERS, ...FORMS, ...ORIENTATION];

const bySlug = new Map(VieraSkillSheets.map(s => [s.slug, s]));

// One mirrored sheet by our slug, or null. Source filename intentionally NOT
// part of the public shape (api strips it) — callers that need it read `.file`.
const getSkillSheet = slug => bySlug.get(slug) || null;

// The checksheet + summary form for an NFPA chapter, for the matrix chapter
// header. Theory-only chapters with no published sheet return both null.
const skillSheetsForChapter = ch => ({
    checksheet: VieraSkillSheets.find(s => s.kind === 'checksheet' && s.nfpaChapter === ch) || null,
    summary: VieraSkillSheets.find(s => s.kind === 'summary' && s.nfpaChapter === ch) || null
});

// Nanaimo source URL for a manifest entry — used only by the fetch script.
const sourceUrl = sheet => `${SOURCE_BASE}${sheet.file}`;

module.exports = {
    VieraSkillSheets,
    SOURCE_BASE,
    CURRENT_EDITION,
    CURRENT_EDITION_LABEL,
    EDITION_RE,
    skillSheetPath,
    skillSheetSkillPath,
    hasSkillSheet,
    getSkillSheet,
    getSkillSheetGeometry,
    skillSheetsForChapter,
    sourceUrl
};
